import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Backend personalizado para cargar JSON por dynamic import (code-splitting)
const dynamicJsonBackend = {
  type: 'backend' as const,
  init() {},
  read: async (
    language: string,
    _namespace: string,
    callback: (err: unknown, data?: unknown) => void
  ) => {
    try {
      const mod = await import(`./locales/${language}/translation.json`);
      callback(null, mod.default);
    } catch (err) {
      callback(err);
    }
  },
};

function detectBrowserLanguage(): 'es' | 'en' {
  if (typeof navigator === 'undefined') return 'es';
  const candidates = [
    navigator.language,
    ...(navigator.languages || []),
  ].filter(Boolean) as string[];
  for (const code of candidates) {
    const lower = code.toLowerCase();
    if (lower.startsWith('es')) return 'es';
    if (lower.startsWith('en')) return 'en';
  }
  return 'es';
}

const storedLang =
  typeof window !== 'undefined' ? localStorage.getItem('lang') : null;
const defaultLng =
  storedLang === 'es' || storedLang === 'en'
    ? storedLang
    : detectBrowserLanguage();

i18n
  .use(
    dynamicJsonBackend as unknown as {
      type: 'backend';
      read: typeof dynamicJsonBackend.read;
    }
  )
  .use(initReactI18next)
  .init({
    lng: defaultLng,
    fallbackLng: 'es',
    interpolation: { escapeValue: false },
    returnNull: false,
    saveMissing: true,
    missingKeyHandler: (_lngs, _ns, key) => {
      // eslint-disable-next-line no-console
      console.warn(`[i18n] Texto faltante para clave: ${String(key)}`);
    },
  })
  .catch(() => {
    // ignore
  });

if (import.meta.env?.DEV) {
  // Advertir si hay claves faltantes entre ES y EN comparando ambos JSON en desarrollo
  Promise.all([
    import('./locales/es/translation.json'),
    import('./locales/en/translation.json'),
  ])
    .then(([es, en]) => {
      function warnMissingKeys(
        base: Record<string, unknown>,
        compare: Record<string, unknown>,
        path: string[] = []
      ) {
        for (const key of Object.keys(base)) {
          const nextPath = [...path, key];
          if (!(key in compare)) {
            // eslint-disable-next-line no-console
            console.warn(
              `[i18n] Falta clave en el otro idioma: ${nextPath.join('.')}`
            );
            continue;
          }
          const baseVal = base[key];
          const cmpVal = compare[key];
          if (
            typeof baseVal === 'object' &&
            baseVal &&
            typeof cmpVal === 'object' &&
            cmpVal
          ) {
            warnMissingKeys(
              baseVal as Record<string, unknown>,
              cmpVal as Record<string, unknown>,
              nextPath
            );
          }
        }
      }
      warnMissingKeys(
        es.default as Record<string, unknown>,
        en.default as Record<string, unknown>
      );
      warnMissingKeys(
        en.default as Record<string, unknown>,
        es.default as Record<string, unknown>
      );
    })
    .catch(() => {
      // ignore
    });
}

export function changeLanguage(lang: 'es' | 'en') {
  localStorage.setItem('lang', lang);
  void i18n.changeLanguage(lang);
}

export default i18n;
